import { useState } from 'react'
import useStore from '../../hooks/use_store'
import { statusTypes } from '../../constants/status_colour_map'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, Menu, Badge, EmptyState, Select, TableFilters } from '@cinesend/ui-kit'
import DataTable from '../data_table'
import RowMenu from '../row_menu'
import { ArrowsClockwise, CloudArrowDown, CloudArrowUp } from '@phosphor-icons/react'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import openInNewTab from '../../helpers/openinnewtab'
import RecallModal from './recall_modal'
import ImportForm from './import_form'
import Page from '../layouts/page'
import useUtilities from '../../hooks/use_utilities'
import CreateAssetButton from './create_asset_button'
import useTableFilters from '../../hooks/use_table_filters'
import dayjs from 'dayjs'
import CycleIsValidatingTag from '../cycles_table/validation_status_tag'
import { CycleStatus } from '../../constants/cycle_statuses'
import { MISSING_UNIQUE_NAME } from '../../constants/category_display'
import { DISABLED_REASONS } from '../../constants/disabled_reasons'
import DisabledTooltip from '../disabled_tooltip'

const formatDate = (date) => (date ? dayjs.utc(date).format('MMM D, YYYY') : 'N/A')

const CLIP_TYPES = [
  { label: 'Warning slate', key: 'warning-slate', value: 'warning-slate' },
  { label: 'Preroll ad', key: 'preroll-ad', value: 'preroll-ad' },
  { label: 'Interstitial ad', key: 'interstitial-ad', value: 'interstitial-ad' },
]

const CLIP_STATUS_OPTIONS = [
  { label: 'New', key: 'new', value: 'new' },
  { label: 'Recalled', key: 'recalled', value: 'recalled' },
  { label: 'Expiring', key: 'expiring', value: 'expiring' },
  { label: 'Holdover', key: 'holdover', value: 'holdover' },
]

const clipTypeLabel = (data) => {
  const fromField = CLIP_TYPES.find((type) => type.value === data.ad_type)
  if (fromField) return fromField.label
  if (data.is_warning_slate) return 'Warning slate'
  if (data.is_preroll_ad) return 'Preroll ad'
  if (data.is_interstitial_ad) return 'Interstitial ad'
  return ''
}

const withoutEmptyFilterValues = (next) =>
  Object.fromEntries(Object.entries(next).filter(([, values]) => !Array.isArray(values) || values.length > 0))

const AssetsTable = ({
  cycle,
  assetType: { value: assetTypeValue, label: assetLabel, plural_label: assetLabelPlural },
}) => {
  const cycleID = cycle.id
  const navigate = useNavigate()
  const { assetTypes } = useUtilities()
  const queryClient = useQueryClient()
  const { createModel, deleteModel } = useStore()
  const [recallModal, setRecallModal] = useState(false)
  const queryKey = `cycles/${cycleID}/assets?assetType=${assetTypeValue}`
  const recallableAssetsQueryKey = `cycles/${cycle.id}/recallable-assets?assetType=${assetTypeValue}`
  const { data: recallableAssets } = useQuery([recallableAssetsQueryKey])
  const [isModalOpen, setIsModalOpen] = useState(false)

  const sortingOptions = [
    { label: 'Status', key: 'status' },
    { label: 'Start Date', key: 'start_date' },
    { label: 'End Date', key: 'end_date' },
    { label: 'Title', key: 'title' },
  ]

  const isClips = assetTypeValue === 'ad'
  const filteringOptions = [
    {
      label: 'Status',
      key: 'type',
      type: 'checkboxes',
      options: CLIP_STATUS_OPTIONS,
    },
    ...(isClips
      ? [
          {
            label: 'Type',
            key: 'ad_type',
            type: 'checkboxes',
            options: CLIP_TYPES,
          },
        ]
      : []),
  ]

  const { queryString, searching, sorting, filtering, pagination } = useTableFilters({
    filteringOptions,
    sortingOptions,
    enableSearch: true,
    queryExists: true,
    persistKey: `assets-table-${cycleID}-${assetTypeValue}`,
  })

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: [queryKey, queryString],
    queryFn: async ({ queryKey }) => {
      const response = await axios.get(`${import.meta.env.VITE_API_URL}/${queryKey[0]}&${queryKey[1]}`)
      return response.data
    },
    refetchInterval: (data) => {
      const shouldPoll = data?.import?.status === 'pending' || data?.import?.status === 'confirmation_in_progress'
      return shouldPoll ? 2000 : false
    },
    refetchIntervalInBackground: false, // only refetch when window is in focus
  })

  const isOpenCycle = cycle.status === CycleStatus.Open

  const openModal = () => setIsModalOpen(true)
  const closeModal = () => {
    queryClient.setQueryData([queryKey, queryString], (oldData) => {
      if (!oldData?.import?.errors) return oldData
      const { errors: _errors, ...importWithoutErrors } = oldData.import
      return { ...oldData, import: importWithoutErrors }
    })
    queryClient.invalidateQueries([queryKey])
    setIsModalOpen(false)
  }
  const goToAsset = (data) => navigate(`${data.id}`)

  const exportAssets = ({ isTemplate }) => {
    createModel.mutate(
      {
        endpoint: 'exports',
        data: {
          name: `export-cycle-${cycleID}-assetType-${assetTypeValue}`,
          cycleID: cycleID,
          type: assetTypeValue,
          isTemplate,
        },
      },
      {
        onSuccess: (res) => {
          if (res.data) {
            axios.get(`${process.env.VITE_API_URL}/exports/${res.data.export}/download`).then((r) => {
              openInNewTab(r.data.url)
            })
          }
        },
      }
    )
  }

  const showValidatorsResult = (data) => {
    return (
      <div className='flex flex-wrap gap-1'>
        {data.errors > 0 && <Badge tone='error'>{`Errors: ${data.errors}`}</Badge>}
        {data.warnings > 0 && <Badge tone='warning'>{`Warnings: ${data.warnings}`}</Badge>}
        {!data.warnings && !data.errors && <Badge tone='success'>OK</Badge>}
      </div>
    )
  }

  const hasPreRoll = !['music', 'podcast', 'audiobook'].includes(assetTypeValue)
  const columns = [
    { key: 'title', header: 'Title', render: (data) => data.default_title },
    { key: 'validators', header: 'Validation', render: (data) => showValidatorsResult(data) },
    {
      key: 'status',
      header: 'Status',
      render: (data) => (
        <Badge tone={statusTypes[data?.status.toLowerCase()] ?? 'neutral'}>{data.status ?? 'N/A'}</Badge>
      ),
    },
    ...(isClips ? [{ key: 'ad_type', header: 'Type', render: (data) => clipTypeLabel(data) }] : []),
    ...(hasPreRoll
      ? [
          {
            key: 'pre_roll',
            header: 'Pre-Roll',
            render: (data) => (
              <div className='py-2 flex flex-wrap gap-2'>
                {data.categoryLabels
                  ?.filter((category) => category.type === 'warning-slates' || category.type === 'ad-pools')
                  .slice()
                  .sort((a, b) => {
                    if (a.type === b.type) return 0
                    return a.type === 'ad-pools' ? -1 : 1
                  })
                  .map((category) => (
                    <Badge key={category.id}>
                      {category.type === 'warning-slates'
                        ? category.value
                        : category.external_unique_name || MISSING_UNIQUE_NAME}
                    </Badge>
                  ))}
              </div>
            ),
          },
        ]
      : []),
    ...(!isClips
      ? [
          {
            key: 'start_date',
            header: 'Start Date',
            render: (data) => formatDate(data.start_date),
          },
          { key: 'end_date', header: 'End Date', render: (data) => formatDate(data.end_date) },
        ]
      : []),
    {
      key: 'actions',
      header: '',
      width: '65px',
      render: (data) =>
        data?.status === 'Holdover' || data?.status === 'Expiring' ? null : (
          <div className='flex justify-end'>
            <RowMenu
              label={`Actions for ${data.title}`}
              items={[
                {
                  id: 'delete',
                  label: 'Delete',
                  tone: 'danger',
                  onSelect: () =>
                    deleteModel.mutate({
                      endpoint: `cycles/${cycleID}/assets`,
                      id: data.id,
                      deleteNoun: assetLabel.toLowerCase(),
                      deleteName: data.title,
                      confirmDetail: assetTypes.some((type) => type.value === data.asset_type && type.is_parent)
                        ? 'Deleting this asset will also delete all child assets.'
                        : undefined,
                      onSuccess: () => queryClient.invalidateQueries(queryKey),
                    }),
                },
              ]}
            />
          </div>
        ),
    },
  ]

  return (
    <Page
      title={
        <>
          {assetLabelPlural} <CycleIsValidatingTag is_validating={cycle?.is_validating} />
        </>
      }
      breadcrumbs={[{ text: assetLabelPlural }]}
      buttons={
        assetTypeValue !== 'featured-content' && [
          <div key={'asset-buttons'} className='flex items-center space-x-2'>
            <CreateAssetButton cycle={cycle} assetType={assetTypeValue} assetLabel={assetLabel} />
            {!isClips && (
              <>
                <Menu
                  trigger={<Button leadingIcon={<CloudArrowDown size={20} />}>Export</Button>}
                  items={[
                    {
                      id: 'export-template',
                      label: 'Export Template',
                      onSelect: () => exportAssets({ isTemplate: true }),
                    },
                    {
                      id: 'export-populated',
                      label: 'Export Populated',
                      onSelect: () => exportAssets({ isTemplate: false }),
                    },
                  ]}
                />
                <DisabledTooltip reason={!isOpenCycle ? DISABLED_REASONS.cycleClosed : null}>
                  <Button
                    leadingIcon={
                      data?.import?.status === 'pending' ? (
                        <ArrowsClockwise size={20} className='animate-spin' />
                      ) : (
                        <CloudArrowUp size={20} />
                      )
                    }
                    onClick={openModal}
                    disabled={!isOpenCycle}
                  >
                    {`Import ${assetTypeValue === 'series' ? 'TV Episodes' : assetLabelPlural}`}
                  </Button>
                </DisabledTooltip>
              </>
            )}

            {recallableAssets?.length > 0 && (
              <Button leadingIcon={<ArrowsClockwise size={20} />} onClick={() => setRecallModal(true)}>
                Recall {assetLabelPlural}
              </Button>
            )}
          </div>,
        ]
      }
    >
      <DataTable
        caption={assetLabelPlural}
        columns={columns}
        rows={data?.data ?? []}
        onRowClick={goToAsset}
        isLoading={isLoading || createModel.isLoading}
        error={isError ? error : null}
        onRetry={refetch}
        empty={
          <EmptyState
            title={`No ${assetLabelPlural.toLowerCase()} found!`}
            detail={`Create a new ${assetLabel.toLowerCase()} to begin.`}
          />
        }
        searching={searching}
        sorting={sorting}
        filtering={filtering}
        filterControl={
          isClips ? (
            <>
              <Select
                label='Type'
                hideLabel
                modal={false}
                value={filtering.filters.ad_type?.[0] ?? 'all'}
                onValueChange={(value) => {
                  const next = { ...filtering.filters }
                  if (!value || value === 'all') delete next.ad_type
                  else next.ad_type = [value]
                  filtering.onFiltersChange(withoutEmptyFilterValues(next))
                }}
                options={[
                  { value: 'all', label: 'All types' },
                  ...CLIP_TYPES.map(({ value, label }) => ({ value, label })),
                ]}
              />
              <TableFilters
                groups={[
                  {
                    key: 'type',
                    label: 'Status',
                    options: CLIP_STATUS_OPTIONS.map(({ value, label }) => ({ value, label })),
                  },
                ]}
                value={{ type: filtering.filters.type ?? [] }}
                onValueChange={(statusFilters) => {
                  const next = { ...filtering.filters }
                  if (statusFilters.type?.length) next.type = statusFilters.type
                  else delete next.type
                  filtering.onFiltersChange(withoutEmptyFilterValues(next))
                }}
              />
            </>
          ) : null
        }
        pagination={{ ...pagination, options: [10, 25, 50, 100, 250, 500, 1000] }}
        totalRows={data?.meta.total ?? 0}
      />
      {isModalOpen && (
        <ImportForm
          onClose={closeModal}
          cycleID={cycleID}
          assetType={assetTypeValue}
          importData={data?.import}
          queryKey={[queryKey, queryString]}
        />
      )}
      {recallModal && (
        <RecallModal
          currentAssetsQueryKey={queryKey}
          recallableAssetsQueryKey={recallableAssetsQueryKey}
          id={cycleID}
          label={assetLabelPlural}
          onClose={() => {
            setRecallModal(false)
            queryClient.invalidateQueries(queryKey)
          }}
        />
      )}
    </Page>
  )
}

export default AssetsTable
